import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon, LockClosedIcon, ChevronLeftIcon, ChevronRightIcon,
  FolderIcon, VideoCameraIcon, MagnifyingGlassIcon, FunnelIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { getNodeModelAssignments, bulkSaveNodeModelAssignments, getNodeCameras, getNodeGroups } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';

interface Model { id: string; name: string; description?: string; }
interface Group { id: string; name: string; }
interface Camera { id: string; name: string; ip_address?: string; location?: string; groups?: any[]; }

const PAGE_SIZE = 30;

export default function ModelAssignmentPage() {
  const { selectedNode } = useNode();
  const nodeId = selectedNode?.nodeId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [models, setModels] = useState<Model[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [cameraMemberships, setCameraMemberships] = useState<any[]>([]);
  const [serverGroupAssignments, setServerGroupAssignments] = useState<any[]>([]);
  const [serverCameraAssignments, setServerCameraAssignments] = useState<any[]>([]);

  const [groupAssignments, setGroupAssignments] = useState<Record<string, Set<string>>>({});
  const [cameraAssignments, setCameraAssignments] = useState<Record<string, Set<string>>>({});

  const [selectedGroup, setSelectedGroup] = useState('all');
  // The group panel is a LIST first — long enough on a real site to need its own filter,
  // independent of the camera search on the right.
  const [groupQuery, setGroupQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [page, setPage] = useState(1);

  const { hasPermission } = useAuth();
  const canAttach = hasPermission('ai-models.assign');

  const fetchData = useCallback(async () => {
    if (!nodeId) return;
    setLoading(true);
    setError('');
    try {
      const [assignData, camerasData, groupsData] = await Promise.all([
        getNodeModelAssignments(nodeId),
        getNodeCameras(nodeId),
        getNodeGroups(nodeId),
      ]);

      const allModels = assignData.models || [];
      const gAssign = assignData.group_assignments || [];
      const cAssign = assignData.camera_assignments || [];
      const memberships = assignData.camera_group_memberships || [];

      setModels(allModels);
      setServerGroupAssignments(gAssign);
      setServerCameraAssignments(cAssign);
      setCameraMemberships(memberships);

      const cams = Array.isArray(camerasData) ? camerasData : camerasData?.data || camerasData?.cameras || [];
      setCameras(cams);

      const grps = Array.isArray(groupsData) ? groupsData : groupsData?.data || [];
      setGroups(grps);

      const gMap: Record<string, Set<string>> = {};
      for (const a of gAssign) {
        if (!gMap[a.group_id]) gMap[a.group_id] = new Set();
        gMap[a.group_id].add(a.model_id);
      }
      setGroupAssignments(gMap);

      const cMap: Record<string, Set<string>> = {};
      for (const a of cAssign) {
        if (!cMap[a.camera_id]) cMap[a.camera_id] = new Set();
        cMap[a.camera_id].add(a.model_id);
      }
      setCameraAssignments(cMap);
      setIsDirty(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /**
   * The group panel's selection is a group ID, so it means nothing on another site: left in
   * place it filters the camera list down to a group this site hasn't got, and the page opens
   * on "no cameras" for a site full of them. (The assignments themselves are replaced by the
   * fetch above, which also clears `isDirty`.)
   */
  const firstNodeRef = useRef(true);
  useEffect(() => {
    if (firstNodeRef.current) { firstNodeRef.current = false; return; }
    setSelectedGroup('all');
    setGroupQuery('');
    setSearchQuery('');
    setPage(1);
  }, [nodeId]);

  const cameraGroupMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    // Use memberships from API if available, fallback to cam.groups
    if (cameraMemberships.length > 0) {
      for (const m of cameraMemberships) {
        if (!map[m.camera_id]) map[m.camera_id] = [];
        map[m.camera_id].push(m.group_id);
      }
    } else {
      for (const cam of cameras) {
        map[cam.id] = cam.groups ? cam.groups.map((g: any) => g.id || g) : [];
      }
    }
    return map;
  }, [cameraMemberships, cameras]);

  const getInheritedModels = useCallback((cameraId: string) => {
    const cameraGroupIds = cameraGroupMap[cameraId] || [];
    const inherited = new Set<string>();
    for (const gid of cameraGroupIds) {
      const mods = groupAssignments[gid];
      if (mods) for (const mid of mods) inherited.add(mid);
    }
    return inherited;
  }, [cameraGroupMap, groupAssignments]);

  const toggleGroupModel = (groupId: string, modelId: string) => {
    setGroupAssignments(prev => {
      const next = { ...prev };
      next[groupId] = new Set(next[groupId] || []);
      if (next[groupId].has(modelId)) next[groupId].delete(modelId);
      else next[groupId].add(modelId);
      return next;
    });
    setIsDirty(true);
  };

  const toggleCameraModel = (cameraId: string, modelId: string) => {
    setCameraAssignments(prev => {
      const next = { ...prev };
      next[cameraId] = new Set(next[cameraId] || []);
      if (next[cameraId].has(modelId)) next[cameraId].delete(modelId);
      else next[cameraId].add(modelId);
      return next;
    });
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!nodeId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const groupPayload: any[] = [];
      for (const [groupId, modelIds] of Object.entries(groupAssignments)) {
        for (const modelId of modelIds) {
          groupPayload.push({ group_id: groupId, model_id: modelId, is_active: true });
        }
      }
      const cameraPayload: any[] = [];
      for (const [cameraId, modelIds] of Object.entries(cameraAssignments)) {
        for (const modelId of modelIds) {
          const inherited = getInheritedModels(cameraId);
          if (inherited.has(modelId)) continue;
          cameraPayload.push({ camera_id: cameraId, model_id: modelId, is_active: true });
        }
      }
      const result = await bulkSaveNodeModelAssignments(nodeId, {
        group_assignments: groupPayload,
        camera_assignments: cameraPayload,
        managed_group_ids: groups.map(g => g.id),
        managed_camera_ids: Object.keys(cameraAssignments),
      });
      setSuccess(`Saved! ${result.affected_cameras} camera(s) updated.`);
      setIsDirty(false);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    const gMap: Record<string, Set<string>> = {};
    for (const a of serverGroupAssignments) {
      if (!gMap[a.group_id]) gMap[a.group_id] = new Set();
      gMap[a.group_id].add(a.model_id);
    }
    setGroupAssignments(gMap);
    const cMap: Record<string, Set<string>> = {};
    for (const a of serverCameraAssignments) {
      if (!cMap[a.camera_id]) cMap[a.camera_id] = new Set();
      cMap[a.camera_id].add(a.model_id);
    }
    setCameraAssignments(cMap);
    setIsDirty(false);
  };

  const filteredCameras = useMemo(() => {
    let cams = cameras;
    if (selectedGroup !== 'all') {
      cams = cams.filter(c => (cameraGroupMap[c.id] || []).includes(selectedGroup));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      cams = cams.filter(c => (c.name || '').toLowerCase().includes(q) || (c.ip_address || '').includes(q) || (c.location || '').toLowerCase().includes(q));
    }
    return cams;
  }, [cameras, selectedGroup, searchQuery, cameraGroupMap]);

  const filteredGroups = useMemo(() => {
    const q = groupQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => (g.name || '').toLowerCase().includes(q));
  }, [groups, groupQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredCameras.length / PAGE_SIZE);
  const paginatedCameras = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredCameras.slice(start, start + PAGE_SIZE);
  }, [filteredCameras, page]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [selectedGroup, searchQuery]);

  if (!nodeId) return <p className="py-16 text-center text-muted-foreground">Select a site first.</p>;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/models" className="rounded-lg p-2 text-muted-foreground hover:bg-muted transition-colors">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Model Assignments</h1>
            <p className="text-[13px] text-muted-foreground">Assign AI models to camera groups and individual cameras</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <button onClick={handleDiscard} className="rounded-lg px-4 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted transition-colors">
              Discard
            </button>
          )}
          {canAttach && (
            <button onClick={handleSave} disabled={!isDirty || saving || !canAttach}
              className="rounded-lg bg-brand px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-dark disabled:opacity-50 transition-colors">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 p-3 text-[13px] text-destructive">{error}</div>}
      {success && <div className="rounded-xl border border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/20 p-3 text-[13px] text-green-700 dark:text-green-400">{success}</div>}

      {/* How it works */}
      <div className="rounded-xl border border-border bg-muted/30 p-3 sm:p-4 text-[12px] text-muted-foreground space-y-1.5">
        <p>
          <span className="font-semibold text-foreground">Group assignment</span> (left panel) applies a model to <span className="font-medium text-foreground">every camera</span> in that group. Those cameras show the model as{' '}
          <span className="inline-flex items-center gap-0.5"><LockClosedIcon className="h-3 w-3" /> inherited</span> and it can’t be toggled off per-camera — remove it from the group instead.
        </p>
        <p>
          <span className="font-semibold text-foreground">Individual assignment</span> (right panel) applies a model to that <span className="font-medium text-foreground">single camera only</span>, on top of anything inherited from its groups.
        </p>
        <p className="flex flex-wrap items-center gap-4 pt-1">
          <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Assigned</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-gray-400" /> Not assigned</span>
          <span className="inline-flex items-center gap-1.5"><LockClosedIcon className="h-3 w-3" /> Inherited from group</span>
        </p>
      </div>

      {/* Main: left groups, right cameras */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* LEFT: Groups panel */}
        <div className="lg:col-span-4 xl:col-span-3">
          <div className="rounded-xl border border-border bg-card shadow-card sticky top-4">
            <div className="px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <FolderIcon className="h-4 w-4 shrink-0 text-brand" />
                <h3 className="text-[13px] font-semibold text-foreground">Group list</h3>
                <span className="ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{groups.length}</span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                A model set here applies to every camera in that group. Click a name to filter the camera list.
              </p>
              {groups.length > 0 && (
                <div className="relative mt-2">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={groupQuery}
                    onChange={(e) => setGroupQuery(e.target.value)}
                    placeholder="Search groups…"
                    className="w-full rounded-md border border-border bg-input py-1 pl-7 pr-2 text-[11px] text-foreground placeholder-muted-foreground focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand-ring"
                  />
                </div>
              )}
            </div>
            <div className="p-3 space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto">
              {groups.length === 0 ? (
                <p className="text-[12px] text-muted-foreground py-6 text-center">No groups found</p>
              ) : filteredGroups.length === 0 ? (
                <div className="py-6 text-center">
                  <p className="text-[12px] text-muted-foreground">No group matches “{groupQuery}”</p>
                  <button onClick={() => setGroupQuery('')} className="mt-1 text-[11px] font-medium text-brand hover:underline">Clear search</button>
                </div>
              ) : (
                filteredGroups.map(group => {
                  const groupModelSet = groupAssignments[group.id] || new Set();
                  const cameraCount = cameras.filter(c => (cameraGroupMap[c.id] || []).includes(group.id)).length;
                  const isActive = selectedGroup === group.id;
                  return (
                    <div
                      key={group.id}
                      className="rounded-r-lg p-3 transition-colors"
                      style={{
                        // Selected = the sidebar's active treatment (brand tint plus a 3px brand
                        // edge), the same signal the nav and the Camera Groups list use, so
                        // "which group am I filtering by" reads identically across the app. The
                        // transparent stripe on the others keeps the rows from shifting.
                        backgroundColor: isActive ? 'var(--sidebar-active-bg)' : undefined,
                        borderLeft: `3px solid ${isActive ? 'var(--sidebar-active-border)' : 'transparent'}`,
                      }}
                    >
                      <button
                        onClick={() => setSelectedGroup(isActive ? 'all' : group.id)}
                        aria-pressed={isActive}
                        title={isActive ? 'Showing only this group — click to show all cameras' : 'Show only this group\u2019s cameras'}
                        className="mb-2 flex w-full items-center gap-1.5 text-left"
                      >
                        {isActive && <FunnelIcon className="h-3 w-3 shrink-0 text-brand" />}
                        <span className={`min-w-0 truncate text-[12px] font-semibold ${isActive ? 'text-brand' : 'text-foreground'}`} title={group.name}>{group.name}</span>
                        <span className="ml-auto flex shrink-0 items-center gap-1">
                          {/* Two numbers an operator actually asks of a group row: how many
                              models it pushes, and how many cameras that reaches. */}
                          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                            groupModelSet.size
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {groupModelSet.size} model{groupModelSet.size === 1 ? '' : 's'}
                          </span>
                          <span
                            className="inline-flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                            title={`${cameraCount} camera${cameraCount === 1 ? '' : 's'} in this group`}
                          >
                            <VideoCameraIcon className="h-3 w-3" />{cameraCount}
                          </span>
                        </span>
                      </button>
                      <div className="flex flex-wrap gap-1.5">
                        {models.map(model => {
                          const active = groupModelSet.has(model.id);
                          return (
                            <button key={model.id} onClick={() => toggleGroupModel(group.id, model.id)}
                              disabled={!canAttach}
                              className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium border transition-all disabled:opacity-60 disabled:cursor-not-allowed ${active
                                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                : 'border-border bg-background text-foreground hover:bg-muted'
                                }`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                              {model.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Camera panel */}
        <div className="lg:col-span-8 xl:col-span-9">
          <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
            {/* Camera header with search + pagination info */}
            <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2 sm:gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <VideoCameraIcon className="h-4 w-4 shrink-0 text-brand" />
                <h3 className="text-[13px] font-semibold text-foreground">Camera list</h3>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{cameras.length}</span>
              </div>
              {selectedGroup !== 'all' && (
                <button onClick={() => setSelectedGroup('all')}
                  title="Clear the group filter"
                  className="inline-flex min-w-0 items-center gap-1 rounded-full bg-brand/10 border border-brand/20 px-2 py-0.5 text-[10px] font-medium text-brand hover:bg-brand/20 transition-colors">
                  <FunnelIcon className="h-3 w-3 shrink-0" />
                  <span className="max-w-[9rem] truncate">{groups.find(g => g.id === selectedGroup)?.name || 'Group'}</span>
                  <XMarkIcon className="h-3 w-3 shrink-0 text-brand/70" />
                </button>
              )}
              <div className="relative ml-auto">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search name, IP, location…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-44 sm:w-56 rounded-md border border-border bg-input py-1 pl-7 pr-2 text-[11px] text-foreground placeholder-muted-foreground focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand-ring"
                />
              </div>
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                {filteredCameras.length === cameras.length
                  ? `${cameras.length} total`
                  : `${filteredCameras.length} of ${cameras.length}`}
              </span>
            </div>

            {/* Camera list */}
            {paginatedCameras.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground">
                <VideoCameraIcon className="mx-auto h-8 w-8 opacity-40" />
                <p className="mt-2 text-[14px]">
                  {searchQuery || selectedGroup !== 'all' ? 'No cameras match these filters' : 'No cameras found'}
                </p>
                {(searchQuery || selectedGroup !== 'all') && (
                  <button
                    onClick={() => { setSearchQuery(''); setSelectedGroup('all'); }}
                    className="mt-2 text-[12px] font-medium text-brand hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {paginatedCameras.map(camera => {
                  const inherited = getInheritedModels(camera.id);
                  const directSet = cameraAssignments[camera.id] || new Set();
                  const cameraGroupIds = cameraGroupMap[camera.id] || [];
                  const cameraGroupNames = cameraGroupIds.map(gid => {
                    const g = groups.find(gr => gr.id === gid);
                    return g ? g.name : '';
                  }).filter(Boolean).join(', ');
                  return (
                    <div key={camera.id} className="flex items-center gap-4 px-4 py-2.5 hover:bg-muted/20 transition-colors">
                      <div className="w-40 sm:w-48 shrink-0">
                        <h4 className="text-[12px] font-medium text-foreground truncate" title={camera.name}>{camera.name}</h4>
                        <p
                          className="text-[10px] text-muted-foreground truncate mt-0.5"
                          title={cameraGroupNames ? `In: ${cameraGroupNames}` : camera.ip_address || undefined}
                        >
                          {cameraGroupNames || camera.ip_address || '—'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5 flex-1">
                        {models.map(model => {
                          const isInherited = inherited.has(model.id);
                          const isChecked = isInherited || directSet.has(model.id);
                          return (
                            <button key={model.id}
                              onClick={() => !isInherited && toggleCameraModel(camera.id, model.id)}
                              disabled={isInherited || !canAttach}
                              title={isInherited ? 'Inherited from group' : model.name}
                              className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium border transition-all ${isInherited
                                ? 'border-border bg-muted/40 text-muted-foreground cursor-default'
                                : isChecked
                                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 cursor-pointer'
                                  : 'border-border bg-background text-foreground hover:bg-muted cursor-pointer'
                                }`}
                            >
                              {isInherited && <LockClosedIcon className="h-2.5 w-2.5" />}
                              {!isInherited && <span className={`h-1.5 w-1.5 rounded-full ${isChecked ? 'bg-emerald-500' : 'bg-gray-400'}`} />}
                              {model.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-2.5 border-t border-border flex items-center justify-between bg-muted/20">
                <span className="text-[11px] text-muted-foreground">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredCameras.length)} of {filteredCameras.length}
                  <span className="hidden sm:inline"> · page {page}/{totalPages}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="rounded-md p-1 text-muted-foreground hover:bg-muted disabled:opacity-30 transition-colors">
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 7) pageNum = i + 1;
                    else if (page <= 4) pageNum = i + 1;
                    else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
                    else pageNum = page - 3 + i;
                    return (
                      <button key={pageNum} onClick={() => setPage(pageNum)}
                        className={`rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors ${page === pageNum ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-muted'}`}>
                        {pageNum}
                      </button>
                    );
                  })}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="rounded-md p-1 text-muted-foreground hover:bg-muted disabled:opacity-30 transition-colors">
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dirty state bar */}
      {isDirty && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card px-6 py-3 flex items-center justify-between shadow-lg">
          <span className="text-[13px] text-amber-600 dark:text-amber-400 font-medium">⚠ You have unsaved changes</span>
          <div className="flex items-center gap-2">
            <button onClick={handleDiscard} className="rounded-lg px-4 py-1.5 text-[12px] font-medium text-muted-foreground hover:bg-muted transition-colors">
              Discard
            </button>

            {canAttach && (
              <button onClick={handleSave} disabled={saving || !canAttach}
                className="rounded-lg bg-brand px-4 py-1.5 text-[12px] font-semibold text-white hover:bg-brand-dark disabled:opacity-50 transition-colors">
                {saving ? 'Saving…' : 'Save'}
              </button>

            )}
          </div>
        </div>
      )}
    </div>
  );
}
